import { getSafeLoginReturnTo } from "./login-navigation";

export type LoginAuthErrorCode =
  | "access_denied"
  | "transaction_expired"
  | "account_not_preauthorized"
  | "account_suspended"
  | "identity_link_failed"
  | "provider_unavailable"
  | "provider_response_invalid"
  | "rate_limited"
  | "configuration_error"
  | "technical_failure";

export type LoginAuthError = {
  code: LoginAuthErrorCode;
  title: string;
  description: string;
  canRetrySso: boolean;
  canUseAlternative: boolean;
  shouldContactSupport: boolean;
};

type ErrorPresentation = Omit<LoginAuthError, "code">;

const PRESENTATIONS: Record<LoginAuthErrorCode, ErrorPresentation> = {
  access_denied: {
    title: "Connexion annulée",
    description:
      "La connexion a été annulée ou refusée par le service gouvernemental.",
    canRetrySso: true,
    canUseAlternative: true,
    shouldContactSupport: false,
  },
  transaction_expired: {
    title: "Tentative de connexion expirée",
    description:
      "Cette tentative ne correspond plus à la session ouverte dans Flux. Recommencez depuis cette page, de préférence dans un seul onglet.",
    canRetrySso: true,
    canUseAlternative: true,
    shouldContactSupport: false,
  },
  account_not_preauthorized: {
    title: "Compte Flux non autorisé",
    description:
      "Aucun compte Flux autorisé ne correspond à votre identité gouvernementale. Assistance Plateforme peut vérifier votre accès.",
    canRetrySso: false,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
  account_suspended: {
    title: "Compte Flux indisponible",
    description:
      "Votre compte Flux ne permet pas actuellement la connexion. Contactez Assistance Plateforme pour faire vérifier votre accès.",
    canRetrySso: false,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
  identity_link_failed: {
    title: "Identité non reliée",
    description:
      "Votre identité gouvernementale n’a pas pu être reliée à votre compte Flux. Assistance Plateforme peut vérifier votre email de connexion.",
    canRetrySso: false,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
  provider_unavailable: {
    title: "Service de connexion indisponible",
    description:
      "Le service gouvernemental de connexion est temporairement indisponible. Vous pouvez réessayer ou utiliser une autre méthode de connexion.",
    canRetrySso: true,
    canUseAlternative: true,
    shouldContactSupport: false,
  },
  provider_response_invalid: {
    title: "Réponse de connexion non vérifiable",
    description:
      "Flux n’a pas pu vérifier la réponse du service gouvernemental. Réessayez, puis contactez Assistance Plateforme si le problème persiste.",
    canRetrySso: true,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
  rate_limited: {
    title: "Trop de tentatives",
    description:
      "Trop de tentatives de connexion ont été effectuées. Patientez quelques minutes avant de réessayer.",
    canRetrySso: false,
    canUseAlternative: true,
    shouldContactSupport: false,
  },
  configuration_error: {
    title: "Connexion gouvernementale indisponible",
    description:
      "La connexion gouvernementale est temporairement indisponible dans Flux. Utilisez une autre méthode ou contactez Assistance Plateforme.",
    canRetrySso: false,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
  technical_failure: {
    title: "Connexion non finalisée",
    description:
      "Flux n’a pas pu finaliser la connexion. Réessayez ou utilisez une autre méthode de connexion.",
    canRetrySso: true,
    canUseAlternative: true,
    shouldContactSupport: true,
  },
};

const LEGACY_ERROR_CODES: Readonly<Record<string, LoginAuthErrorCode>> = {
  state_not_found: "transaction_expired",
  state_mismatch: "transaction_expired",
  invalid_state: "transaction_expired",
  signup_disabled: "account_not_preauthorized",
  user_not_found: "account_not_preauthorized",
  user_banned: "account_suspended",
  account_not_linked: "identity_link_failed",
  unable_to_link_account: "identity_link_failed",
  account_already_linked: "identity_link_failed",
  temporarily_unavailable: "provider_unavailable",
  server_error: "provider_unavailable",
  invalid_request: "provider_response_invalid",
  invalid_grant: "provider_response_invalid",
  invalid_token: "provider_response_invalid",
  too_many_requests: "rate_limited",
  invalid_client: "configuration_error",
};

const PUBLIC_ERROR_CODES = new Set<LoginAuthErrorCode>(
  Object.keys(PRESENTATIONS) as LoginAuthErrorCode[],
);

function resolvePublicCode(value: string | null): LoginAuthErrorCode {
  const normalized = value?.trim().toLowerCase().replaceAll("-", "_") ?? "";
  if (PUBLIC_ERROR_CODES.has(normalized as LoginAuthErrorCode)) {
    return normalized as LoginAuthErrorCode;
  }
  return LEGACY_ERROR_CODES[normalized] ?? "technical_failure";
}

export function resolveLoginAuthError(
  params: URLSearchParams,
): LoginAuthError | null {
  const error = params.get("error");
  const authError = params.get("authError")?.trim().toLowerCase();
  if (!error && !authError) {
    return null;
  }

  const code =
    authError === "sso" ? resolvePublicCode(error) : "technical_failure";
  return {
    code,
    ...PRESENTATIONS[code],
  };
}

export function buildCleanLoginUrl(params: URLSearchParams) {
  const clean = new URLSearchParams();
  const returnTo = getSafeLoginReturnTo(`?${params.toString()}`);
  if (returnTo) {
    clean.set("returnTo", returnTo);
  }
  const query = clean.toString();
  return query ? `/login?${query}` : "/login";
}
